import styled from "@emotion/styled"
import { PButton, PInlineNotification, PText } from "@porsche-design-system/components-react"
import { spacingStaticMd } from "@porsche-design-system/components-react/emotion"
import { cancelDosSaleRoute } from "@slfinrtl/ui-bff-routes"
import { useCallback, useContext, useState } from "react"
import { defineMessages, FormattedMessage, useIntl } from "react-intl"
import { commonMessages } from "src/common/messages/common.messages"
import { ButtonGroup } from "src/components"
import { Modal } from "src/context/modal/Modal"
import { ModalContext } from "src/context/modal/ModalContext"
import type { ModalProps } from "src/context/modal/ModalProvider"
import { useBff } from "src/hooks"

const messages = defineMessages({
	abortButton: {
		defaultMessage: "Not now",
		id: "cancelSale.modal.abort",
	},
	cancelSaleFailedDescription: {
		defaultMessage: "There was an error cancelling the sale of this vehicle, please try again.",
		id: "cancelSale.modal.cancelSaleFailedText",
	},
	cancelSaleFailedHeader: {
		defaultMessage: "Sale could not be cancelled.",
		id: "cancelSale.modal.cancelSaleFailedHeader",
	},
	content: {
		defaultMessage: "Would you really like to cancel the sale for this vehicle?",
		id: "cancelSale.modal.content",
	},
	headline: {
		defaultMessage: "Cancel vehicle sale",
		id: "cancelSale.modal.headline",
	},
	proceedButton: {
		defaultMessage: "Cancel sale",
		id: "cancelSale.modal.proceed",
	},
})

interface CancelSaleModalContainerProps {
	inventoryId: string
	offerId: string
	onSaleCancelled: () => void
}

type Props = ModalProps<CancelSaleModalContainerProps>
export const CancelSaleModalContainer = ({ id, modalProps }: Props) => {
	const { hideModal } = useContext(ModalContext)
	const { formatMessage } = useIntl()

	const [hasError, setHasError] = useState(false)
	const [isCancellingSale, setIsCancellingSale] = useState(false)
	const [isRetrying, setIsRetrying] = useState(false)

	const cancelSaleBff = useBff(
		cancelDosSaleRoute,
		{
			body: undefined,
			pathParams: {
				inventoryId: modalProps.inventoryId,
				offerId: modalProps.offerId,
			},
			queryParams: {},
		},
		"none",
	)

	const onCancelSaleSuccess = useCallback(() => {
		modalProps.onSaleCancelled()
		hideModal(id)
	}, [modalProps, hideModal, id])

	const onError = () => {
		setIsCancellingSale(false)
		setIsRetrying(false)
		setHasError(true)
	}

	const cancelSaleAndHideModal = useCallback(() => {
		cancelSaleBff().then(onCancelSaleSuccess).catch(onError)
	}, [cancelSaleBff, onCancelSaleSuccess])

	const onCancelSale = () => {
		setIsCancellingSale(true)
		setHasError(false)
		setIsRetrying(false)
		cancelSaleAndHideModal()
	}

	const onRetry = () => {
		setIsRetrying(true)
		cancelSaleAndHideModal()
	}

	return (
		<Modal id={id} heading={formatMessage(messages.headline)} dismissButton={false}>
			<PText>
				<FormattedMessage {...messages.content} />
			</PText>
			{hasError && (
				<StyledInlineNotification
					heading={formatMessage(messages.cancelSaleFailedHeader)}
					description={formatMessage(messages.cancelSaleFailedDescription)}
					state="error"
					dismissButton={false}
					actionLabel={formatMessage(commonMessages.retry)}
					actionIcon="refresh"
					actionLoading={isRetrying}
					onAction={onRetry}
				/>
			)}

			<ButtonGroup>
				<PButton loading={isCancellingSale} onClick={onCancelSale} disabled={hasError}>
					<FormattedMessage {...messages.proceedButton} />
				</PButton>
				<PButton type="button" variant="secondary" onClick={() => hideModal(id)}>
					<FormattedMessage {...messages.abortButton} />
				</PButton>
			</ButtonGroup>
		</Modal>
	)
}

const StyledInlineNotification = styled(PInlineNotification)`
	margin-top: ${spacingStaticMd};
`
